package br.com.vvrefeicoes.portal.feature.satisfaction

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.PdfLine
import br.com.vvrefeicoes.portal.core.util.PdfReport
import br.com.vvrefeicoes.portal.core.util.PdfWriter
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.core.util.encodeBase64
import br.com.vvrefeicoes.portal.core.util.isFileExportSupported
import br.com.vvrefeicoes.portal.core.util.isPdfExportSupported
import br.com.vvrefeicoes.portal.core.util.saveFile
import br.com.vvrefeicoes.portal.core.util.savePdf
import br.com.vvrefeicoes.portal.domain.model.OperationStatus
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.SatisfactionCompany
import br.com.vvrefeicoes.portal.domain.model.SatisfactionCompanyService
import br.com.vvrefeicoes.portal.domain.model.SatisfactionMetric
import br.com.vvrefeicoes.portal.domain.model.SatisfactionRankingItem
import br.com.vvrefeicoes.portal.domain.model.SatisfactionReport
import br.com.vvrefeicoes.portal.domain.model.SatisfactionServiceVotes
import br.com.vvrefeicoes.portal.feature.operacional.ChartCard
import br.com.vvrefeicoes.portal.feature.operacional.DonutChart
import br.com.vvrefeicoes.portal.feature.operacional.DonutSlice
import br.com.vvrefeicoes.portal.feature.operacional.LegendEntry
import br.com.vvrefeicoes.portal.feature.operacional.SeriesLegend
import br.com.vvrefeicoes.portal.feature.operacional.ValueBar
import br.com.vvrefeicoes.portal.feature.operacional.rememberValueAxis
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Search
import io.github.koalaplot.core.bar.DefaultBarPlotGroupedPointEntry
import io.github.koalaplot.core.bar.DefaultBarPosition
import io.github.koalaplot.core.bar.GroupedVerticalBarPlot
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import kotlinx.coroutines.CancellationException
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlin.math.round

private val excellentColor = Color(0xFF22C55E)
private val goodColor = Color(0xFF3B82F6)
private val regularColor = Color(0xFFF59E0B)
private val poorColor = Color(0xFFEF4444)

private enum class SatisfactionPeriodMode(
    val label: String,
) {
    DAY("Dia"),
    WEEK("Semana"),
    MONTH("Mês"),
    CUSTOM("Customizado"),
}

@Composable
fun SatisfactionReportsScreen() {
    val repository = LocalAppGraph.current.satisfactionReportsRepository
    val filters = LocalGlobalFilters.current
    val session by LocalAppGraph.current.authRepository.session
        .collectAsStateWithLifecycle()
    val canSeeAllUnits = PermissionKeys.SEE_ALL_UNITS in session?.permissions.orEmpty()
    val today = remember { SystemDateTimeProvider().today() }
    var companies by remember { mutableStateOf<List<SatisfactionCompany>>(emptyList()) }
    var selectedCompany by remember { mutableStateOf<SatisfactionCompany?>(null) }
    var report by remember { mutableStateOf<SatisfactionReport?>(null) }
    var periodMode by remember { mutableStateOf(SatisfactionPeriodMode.DAY) }
    var customStart by remember { mutableStateOf(today.toString()) }
    var customEnd by remember { mutableStateOf(today.toString()) }
    var appliedStart by remember { mutableStateOf(today.toString()) }
    var appliedEnd by remember { mutableStateOf(today.toString()) }
    var error by remember { mutableStateOf<String?>(null) }
    var exportMessage by remember { mutableStateOf<String?>(null) }
    val scopedCompanies =
        if (canSeeAllUnits) {
            companies
        } else {
            companies.filter { company -> filters.units.any { it.name.equals(company.name, ignoreCase = true) } }
        }.sortedBy { it.name.lowercase() }

    LaunchedEffect(Unit) {
        try {
            companies = repository.loadCompanies().filter { it.active }
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Exception) {
            error = exception.message ?: "Não foi possível carregar as empresas do Satisfaction."
        }
    }
    LaunchedEffect(scopedCompanies, filters.selectedUnit) {
        if (selectedCompany !in scopedCompanies) selectedCompany = null
    }
    LaunchedEffect(selectedCompany, appliedStart, appliedEnd) {
        val company = selectedCompany ?: return@LaunchedEffect
        try {
            report = repository.loadReport(company.id, appliedStart, appliedEnd)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Exception) {
            error = exception.message ?: "Não foi possível carregar o relatório."
        }
    }

    SatisfactionPage("Relatórios", error) {
        ExportActions(
            onCsv = { exportMessage = exportReportCsv(selectedCompany, report) },
            onPdf = { exportMessage = exportReportPdf(selectedCompany, report) },
            enabled = report != null,
        )
        exportMessage?.let { Text(it, color = MaterialTheme.colorScheme.onSurfaceVariant) }
        Text("Analise a satisfação por empresa e período.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        CompanyPicker(scopedCompanies, selectedCompany) { selectedCompany = it }
        if (selectedCompany == null) {
            EmptySatisfactionState("Selecione uma empresa", "Escolha uma empresa para visualizar os relatórios de satisfação.")
        } else {
            SatisfactionPeriodFilter(periodMode, customStart, customEnd) { mode, start, end ->
                periodMode = mode
                if (start != null && end != null) {
                    customStart = start
                    customEnd = end
                    appliedStart = start
                    appliedEnd = end
                } else {
                    val bounds = satisfactionPeriodBounds(mode, today)
                    appliedStart = bounds.first
                    appliedEnd = bounds.second
                }
            }
            val current = report
            if (current == null) {
                Text("Carregando dados...", modifier = Modifier.padding(vertical = AppSpacing.space24))
            } else {
                ReportContent(current, periodDaysBetween(appliedStart, appliedEnd), selectedCompany!!, appliedStart, appliedEnd)
            }
        }
    }
}

@Composable
fun SatisfactionOperationStatusScreen() {
    val repository = LocalAppGraph.current.satisfactionReportsRepository
    val filters = LocalGlobalFilters.current
    val session by LocalAppGraph.current.authRepository.session
        .collectAsStateWithLifecycle()
    val canSeeAllUnits = PermissionKeys.SEE_ALL_UNITS in session?.permissions.orEmpty()
    val today = remember { SystemDateTimeProvider().today() }
    val initialMonthBounds = remember(today) { LocalDate(today.year, today.month, 1).toString() to today.toString() }
    var periodMode by remember { mutableStateOf(SatisfactionPeriodMode.MONTH) }
    var customStart by remember { mutableStateOf(initialMonthBounds.first) }
    var customEnd by remember { mutableStateOf(initialMonthBounds.second) }
    var appliedStart by remember { mutableStateOf(initialMonthBounds.first) }
    var appliedEnd by remember { mutableStateOf(initialMonthBounds.second) }
    var status by remember { mutableStateOf<OperationStatus?>(null) }
    var ranking by remember { mutableStateOf<List<SatisfactionRankingItem>>(emptyList()) }
    var companies by remember { mutableStateOf<List<SatisfactionCompany>>(emptyList()) }
    var error by remember { mutableStateOf<String?>(null) }
    var exportMessage by remember { mutableStateOf<String?>(null) }
    val scopedCompanyIds =
        companies
            .filter { company -> canSeeAllUnits || filters.units.any { it.name.equals(company.name, ignoreCase = true) } }
            .map { it.id }
            .toSet()

    LaunchedEffect(Unit) {
        try {
            companies = repository.loadCompanies().filter { it.active }
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Exception) {
            error = exception.message ?: "Não foi possível carregar as empresas do organograma."
        }
    }
    LaunchedEffect(appliedStart, appliedEnd, scopedCompanyIds) {
        try {
            val operationStatus = repository.loadOperationStatus(appliedStart, appliedEnd, null)
            val completeRanking = repository.loadRanking(appliedStart, appliedEnd, null)
            val scopedIds = scopedCompanyIds
            ranking = completeRanking.filter { it.companyId in scopedIds }
            status = if (ranking.size == completeRanking.size) operationStatus else ranking.toOperationStatus()
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Exception) {
            error = exception.message ?: "Não foi possível carregar o status da operação."
        }
    }

    SatisfactionPage("Status da Operação", error) {
        ExportActions(
            onCsv = { exportMessage = exportRankingCsv(ranking, appliedStart, appliedEnd) },
            onPdf = { exportMessage = exportRankingPdf(ranking, appliedStart, appliedEnd) },
            enabled = ranking.isNotEmpty(),
        )
        exportMessage?.let { Text(it, color = MaterialTheme.colorScheme.onSurfaceVariant) }
        Text("Métricas gerais e ranking de satisfação por período.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        SatisfactionPeriodFilter(periodMode, customStart, customEnd) { mode, start, end ->
            periodMode = mode
            if (start != null && end != null) {
                customStart = start
                customEnd = end
                appliedStart = start
                appliedEnd = end
            } else {
                val bounds = satisfactionPeriodBounds(mode, today)
                appliedStart = bounds.first
                appliedEnd = bounds.second
            }
        }
        Text(
            "Período analisado: $appliedStart a $appliedEnd",
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        status?.let {
            OperationContent(
                status = it,
                ranking = ranking,
                companies = companies,
                startDate = appliedStart,
                endDate = appliedEnd,
                periodDays = periodDaysBetween(appliedStart, appliedEnd),
            )
        }
    }
}

@Composable
private fun ReportContent(
    report: SatisfactionReport,
    periodDays: Int,
    company: SatisfactionCompany,
    startDate: String,
    endDate: String,
) {
    val positive = (report.ratings["Ótimo"] ?: 0) + (report.ratings["Bom"] ?: 0)
    val satisfaction = if (report.totalVotes == 0) 0.0 else positive * 100.0 / report.totalVotes
    val ratingLabels = listOf("Ótimo", "Bom", "Regular", "Ruim").filterNot { company.buttonCount == 3 && it == "Ruim" }
    SectionTitle("Relatório de Satisfação - ${company.name} (${company.buttonCount} botões)")
    Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12), modifier = Modifier.fillMaxWidth()) {
        MetricCard("Total de votos", report.totalVotes.toString(), "Média diária: ${decimal(report.totalVotes.toDouble() / periodDays)}", goodColor, Modifier.weight(1f))
        MetricCard("Taxa de satisfação", "${decimal(satisfaction)}%", satisfactionLevel(satisfaction), excellentColor, Modifier.weight(1f))
        MetricCard("Ótimo", (report.ratings["Ótimo"] ?: 0).toString(), "Avaliações", excellentColor, Modifier.weight(1f))
    }
    Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12), modifier = Modifier.fillMaxWidth()) {
        MetricCard("Bom", (report.ratings["Bom"] ?: 0).toString(), "Avaliações", goodColor, Modifier.weight(1f))
        MetricCard("Regular", (report.ratings["Regular"] ?: 0).toString(), "Avaliações", regularColor, Modifier.weight(1f))
        if (company.buttonCount != 3) {
            MetricCard("Ruim", (report.ratings["Ruim"] ?: 0).toString(), "Avaliações", poorColor, Modifier.weight(1f))
        }
    }
    DailySurveyChart(report)
    Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12), modifier = Modifier.fillMaxWidth()) {
        ChartCard("Distribuição de avaliações", Modifier.weight(1f)) {
            val slices =
                ratingLabels
                    .map { it to ratingColor(it) }
                    .mapNotNull { (label, color) ->
                        report.ratings[label]?.takeIf { it > 0 }?.let { DonutSlice(label, it.toLong(), color) }
                    }
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16), verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
                DonutChart(slices, Modifier.size(150.dp), formatValue = Long::toString)
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                    ratingLabels.map { label -> label to ratingColor(label) }.forEach { (label, color) ->
                        val value = report.ratings[label] ?: 0
                        val percentage = report.percentages[label] ?: 0.0
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            SeriesLegend(listOf(LegendEntry(color, label)))
                            Text("$value (${decimal(percentage)}%)", color = MaterialTheme.colorScheme.onSurfaceVariant)
                        }
                    }
                }
            }
        }
        ChartCard("Resultado do período", Modifier.weight(1f)) {
            ratingLabels.forEach { label ->
                val value = report.percentages[label] ?: 0.0
                Text("$label: ${decimal(value)}%", modifier = Modifier.padding(vertical = 3.dp))
            }
        }
    }
    ReportCard("Pesquisa diária") {
        report.dailyVotes.forEach { day ->
            val satisfied = day.excellent + day.good
            val needsImprovement = day.regular + day.poor
            Text(formatSatisfactionDate(day.date), style = MaterialTheme.typography.titleSmall)
            BarRow("Satisfeito", satisfied, day.total, excellentColor)
            BarRow("Melhorar", needsImprovement, day.total, poorColor)
        }
    }
    ServiceAnalysis(report, company, startDate, endDate)
    DetailedDailyReport(report, company)
    ChartCard("Relatório de respostas negativas") {
        if (report.negativeVotes.isEmpty()) Text("Nenhuma resposta negativa no período.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        report.negativeVotes.forEach { vote ->
            Column(Modifier.fillMaxWidth().padding(vertical = 6.dp)) {
                Text("${vote.rating} · ${vote.serviceName.ifBlank { "Serviço" }}", style = MaterialTheme.typography.titleSmall)
                Text(vote.comment.ifBlank { "Sem comentário" }, color = MaterialTheme.colorScheme.onSurfaceVariant)
                Text(formatSatisfactionDate(vote.date), style = MaterialTheme.typography.bodySmall)
            }
            HorizontalDivider()
        }
    }
    ChartCard("Respostas mais recentes") {
        if (report.recentVotes.isEmpty()) Text("Nenhuma resposta recente no período.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        report.recentVotes.forEach { vote ->
            Row(Modifier.fillMaxWidth().padding(vertical = 6.dp), horizontalArrangement = Arrangement.SpaceBetween) {
                Column(Modifier.weight(1f)) {
                    Text("${vote.rating} · ${vote.serviceName.ifBlank { "Serviço" }}", style = MaterialTheme.typography.titleSmall)
                    Text(vote.comment.ifBlank { "Sem comentário" }, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
                Text(formatSatisfactionDate(vote.date), style = MaterialTheme.typography.bodySmall)
            }
            HorizontalDivider()
        }
    }
}

@Composable
private fun DailySurveyChart(report: SatisfactionReport) {
    if (report.dailyVotes.isEmpty()) return
    val labels = report.dailyVotes.map { formatSatisfactionDate(it.date).take(5) }
    val data =
        report.dailyVotes.mapIndexed { index, day ->
            DefaultBarPlotGroupedPointEntry(
                i = labels[index],
                d =
                    listOf(
                        DefaultBarPosition(0f, (day.excellent + day.good).toFloat()),
                        DefaultBarPosition(0f, (day.regular + day.poor).toFloat()),
                    ),
            )
        }
    val maxValue =
        report.dailyVotes
            .maxOf { maxOf(it.excellent + it.good, it.regular + it.poor) }
            .coerceAtLeast(1)
            .toFloat()
    ChartCard("Pesquisa diária") {
        SeriesLegend(listOf(LegendEntry(excellentColor, "Satisfeito"), LegendEntry(poorColor, "Melhorar")))
        XYGraph(
            gridStyle = NoGridStyle,
            xAxisModel = CategoryAxisModel(labels),
            yAxisModel = rememberValueAxis(maxValue, headroom = 1.2f),
            xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
            yAxisContent = rememberAxisContent(labels = { value -> Text(value.toString(), style = MaterialTheme.typography.labelSmall) }),
            modifier = Modifier.fillMaxWidth().height(260.dp),
        ) {
            GroupedVerticalBarPlot(
                data = data,
                maxBarGroupWidth = 0.7f,
                bar = { _, seriesIndex, value ->
                    ValueBar(
                        color = if (seriesIndex == 0) excellentColor else poorColor,
                        label = (value.d.getOrNull(seriesIndex)?.end ?: 0f).toInt().toString(),
                        horizontal = false,
                    )
                },
            )
        }
    }
}

private data class ServiceGoalMetric(
    val service: SatisfactionServiceVotes,
    val expected: Double,
) {
    val difference: Double get() = kotlin.math.abs(expected - service.total)
    val hasGoal: Boolean get() = expected > 0.0
    val reached: Boolean get() = hasGoal && service.total >= expected
}

@Composable
private fun ServiceAnalysis(
    report: SatisfactionReport,
    company: SatisfactionCompany,
    startDate: String,
    endDate: String,
) {
    val services = report.serviceVotes.sortedBy { it.name.lowercase() }
    val goalServices = services.filter(::isGoalService)
    val metrics = goalServices.map { ServiceGoalMetric(it, expectedServiceGoal(it, startDate, endDate)) }
    val totalVotes = metrics.sumOf { it.service.total }
    val totalGoal = metrics.sumOf { it.expected }
    val totalDifference = kotlin.math.abs(totalGoal - totalVotes)
    val totalHasGoal = totalGoal > 0.0
    val totalReached = totalHasGoal && totalVotes >= totalGoal
    val lowestSample = metrics.minByOrNull { it.service.total }
    val hiddenRatings = company.buttonCount == 3

    ChartCard("Análise por serviço") {
        Text("Métricas por refeição (Desjejum, Almoço, Ceia e outros).", color = MaterialTheme.colorScheme.onSurfaceVariant)
        FlowRow(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.CenterHorizontally),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            maxItemsInEachRow = 5,
        ) {
            ServiceSummaryTile("Avaliações recebidas", totalVotes.toString(), goodColor)
            ServiceSummaryTile("Meta esperada", decimal(totalGoal), goodColor)
            ServiceSummaryTile("Diferença", decimal(totalDifference), regularColor)
            ServiceSummaryTile("Meta", "30%", goodColor)
            ServiceSummaryTile(
                "Status",
                if (!totalHasGoal) {
                    "—"
                } else if (totalReached) {
                    "Atingiu"
                } else {
                    "Não atingiu"
                },
                if (totalReached) excellentColor else poorColor,
            )
        }
        Row(
            Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.tertiaryContainer, AppShapes.medium).padding(AppSpacing.space12),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            Text("Insights", style = MaterialTheme.typography.titleSmall)
            Text(
                lowestSample?.let { "Menor amostragem: ${it.service.total} avaliações para meta de ${decimal(it.expected)}." }
                    ?: "Sem dados de amostragem no período.",
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        if (services.isEmpty()) {
            Text("Nenhum serviço identificado no período.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        } else {
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.CenterHorizontally),
                verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                maxItemsInEachRow = 4,
            ) {
                services.forEach { service ->
                    ServiceCard(
                        metric = ServiceGoalMetric(service, expectedServiceGoal(service, startDate, endDate)),
                        hidePoor = hiddenRatings,
                    )
                }
            }
        }
    }
}

@Composable
private fun ServiceSummaryTile(
    label: String,
    value: String,
    color: Color,
) {
    Card(Modifier.width(180.dp), border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)) {
        Column(Modifier.padding(AppSpacing.space12)) {
            Text(label, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Text(value, style = MaterialTheme.typography.titleLarge, color = color)
        }
    }
}

@Composable
private fun ServiceCard(
    metric: ServiceGoalMetric,
    hidePoor: Boolean,
) {
    val service = metric.service
    val ratings = listOf("Ótimo", "Bom", "Regular", "Ruim").filterNot { hidePoor && it == "Ruim" }
    Card(Modifier.width(300.dp), border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)) {
        Column(Modifier.padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            Text("${serviceEmoji(service.name)} ${service.name.ifBlank { "Serviço não identificado" }}", style = MaterialTheme.typography.titleMedium)
            val time = listOf(service.startTime, service.endTime).filter { it.isNotBlank() }.joinToString(" - ")
            if (time.isNotBlank()) Text(time, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Text("Resultado da satisfação", style = MaterialTheme.typography.titleSmall)
            ratings.forEach { rating ->
                val count = service.ratings[rating] ?: 0
                val percentage = servicePercent(service, rating)
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Text(rating, modifier = Modifier.width(70.dp), style = MaterialTheme.typography.bodySmall)
                    LinearProgressIndicator(
                        progress = { (percentage / 100.0).coerceIn(0.0, 1.0).toFloat() },
                        modifier = Modifier.weight(1f).height(8.dp),
                        color = ratingColor(rating),
                    )
                    Text("$count (${decimal(percentage)}%)", modifier = Modifier.width(90.dp), style = MaterialTheme.typography.labelSmall, textAlign = TextAlign.End)
                }
            }
            val approval = servicePercent(service, "Ótimo") + servicePercent(service, "Bom")
            Text("Índice de aprovação: ${decimal(approval)}%", color = ratingColor(if (approval >= 60) "Ótimo" else "Ruim"))
            ServiceMetricLine("Avaliações recebidas", service.total.toString())
            ServiceMetricLine("Meta esperada", decimal(metric.expected))
            ServiceMetricLine("Diferença", decimal(metric.difference))
            ServiceMetricLine("Meta", "30%")
            ServiceMetricLine(
                "Status",
                if (!metric.hasGoal) {
                    "—"
                } else if (metric.reached) {
                    "Atingiu a meta"
                } else {
                    "Não atingiu a meta"
                },
            )
        }
    }
}

@Composable
private fun ServiceMetricLine(
    label: String,
    value: String,
) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, color = MaterialTheme.colorScheme.onSurfaceVariant, style = MaterialTheme.typography.bodySmall)
        Text(value, style = MaterialTheme.typography.bodySmall)
    }
}

@Composable
private fun DetailedDailyReport(
    report: SatisfactionReport,
    company: SatisfactionCompany,
) {
    ChartCard("Relatório detalhado por dia") {
        if (report.dailyVotes.isEmpty()) {
            Text("Nenhum registro no período selecionado.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        } else {
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val tableWidth = 760.dp
                val viewportWidth = maxWidth
                Row(Modifier.fillMaxWidth().horizontalScroll(rememberScrollState())) {
                    Box(
                        modifier = Modifier.width(maxOf(viewportWidth, tableWidth)),
                        contentAlignment = Alignment.Center,
                    ) {
                        Column(Modifier.width(tableWidth)) {
                            Row(Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.surfaceVariant, AppShapes.small).padding(vertical = 9.dp)) {
                                TableHeader("Data", 120.dp)
                                TableHeader("Empresa", 220.dp)
                                TableHeader("Ótimo", 80.dp)
                                TableHeader("Bom", 80.dp)
                                TableHeader("Regular", 90.dp)
                                if (company.buttonCount != 3) TableHeader("Ruim", 80.dp)
                                TableHeader("Total", 80.dp)
                            }
                            report.dailyVotes.forEach { day ->
                                Row(Modifier.fillMaxWidth().padding(vertical = 9.dp)) {
                                    TableCell(formatSatisfactionDate(day.date), 120.dp)
                                    TableCell(company.name, 220.dp)
                                    TableCell(day.excellent.toString(), 80.dp)
                                    TableCell(day.good.toString(), 80.dp)
                                    TableCell(day.regular.toString(), 90.dp)
                                    if (company.buttonCount != 3) TableCell(day.poor.toString(), 80.dp)
                                    TableCell(day.total.toString(), 80.dp)
                                }
                                HorizontalDivider()
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ExportActions(
    onCsv: () -> Unit,
    onPdf: () -> Unit,
    enabled: Boolean,
) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
        Button(onClick = onCsv, enabled = enabled && isFileExportSupported()) { Text("Exportar CSV") }
        Button(onClick = onPdf, enabled = enabled && isPdfExportSupported()) { Text("Exportar PDF") }
    }
}

@Composable
private fun OperationContent(
    status: OperationStatus,
    ranking: List<SatisfactionRankingItem>,
    companies: List<SatisfactionCompany>,
    startDate: String,
    endDate: String,
    periodDays: Int,
) {
    Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12), modifier = Modifier.fillMaxWidth()) {
        MetricCard("Total de votos", status.totalVotes.toString(), "No período", goodColor, Modifier.weight(1f))
        MetricCard("Satisfação", "${decimal(status.satisfaction.percentage)}%", "${status.satisfaction.count} votos positivos", excellentColor, Modifier.weight(1f))
        MetricCard("Melhoria", "${decimal(status.improvement.percentage)}%", "${status.improvement.count} avaliações", regularColor, Modifier.weight(1f))
    }
    val companiesById = companies.associateBy { it.id }
    val rows =
        ranking.mapNotNull { item ->
            val company = companiesById[item.companyId] ?: return@mapNotNull null
            val goals = getCompanyGoalsForPeriod(company, startDate, endDate, periodDays, item.dailyMeals)
            OperationRankingRow(item = item, goals = goals)
        }
    val reached =
        rows
            .filter { it.goals.metaPeriodo > 0.0 && it.item.totalVotes >= it.goals.metaPeriodo }
            .sortedByDescending { it.achievedPercentage }
    val below = rows.filterNot { it in reached }.sortedByDescending { it.achievedPercentage }
    RankingTable("Empresas que atingiram a meta do período", reached, excellentColor)
    RankingTable("Empresas abaixo da meta do período", below, regularColor)
}

private data class OperationRankingRow(
    val item: SatisfactionRankingItem,
    val goals: CompanyPeriodGoals,
) {
    val achievedPercentage: Double
        get() = if (goals.metaPeriodoTotal == 0.0) 0.0 else item.totalVotes * 100.0 / goals.metaPeriodoTotal
}

private data class CompanyPeriodGoals(
    val metaDia: Double,
    val metaPeriodo: Double,
    val metaPeriodoTotal: Double,
)

private const val LUNCH_SERVICE_TYPE_ID = "73c1106b-7a16-4b90-8724-e82467cee9c6"
private const val DINNER_SERVICE_TYPE_ID = "7c0cd516-6afb-4313-8e28-3bbda0add33a"

private fun normalizeMealText(value: String): String =
    value
        .uppercase()
        .replace('Á', 'A')
        .replace('À', 'A')
        .replace('Ã', 'A')
        .replace('Â', 'A')
        .replace('É', 'E')
        .replace('Ê', 'E')
        .replace('Í', 'I')
        .replace('Ó', 'O')
        .replace('Õ', 'O')
        .replace('Ô', 'O')
        .replace('Ú', 'U')
        .replace('Ç', 'C')

private fun isBigMealService(service: SatisfactionCompanyService): Boolean {
    if (service.typeId == LUNCH_SERVICE_TYPE_ID || service.typeId == DINNER_SERVICE_TYPE_ID) return true
    val serviceText = normalizeMealText("${service.name} ${service.typeId}")
    return serviceText.contains("ALMOCO") || serviceText.contains("JANTAR") || serviceText.contains("JANTA")
}

private fun getCompanyGoalsForPeriod(
    company: SatisfactionCompany,
    startDate: String,
    endDate: String,
    periodDays: Int,
    dailyGoalFromRanking: Double,
): CompanyPeriodGoals {
    val activeServices = company.services.filter { it.active }
    val goalServices = activeServices.filter(::isBigMealService)
    var periodQuantity = 0.0
    val serviceDays = mutableSetOf<String>()

    if (activeServices.isNotEmpty()) {
        var current = LocalDate.parse(startDate)
        val end = LocalDate.parse(endDate)
        while (current <= end) {
            goalServices.forEach { service ->
                val activeDays = service.activeDaysByMonth
                val monthKey = current.monthNumber.toString()
                val isActive =
                    if (activeDays.containsKey(monthKey)) {
                        current.dayOfMonth in activeDays.getValue(monthKey)
                    } else {
                        true
                    }
                if (!isActive) return@forEach

                val dayMultiplier =
                    when (current.dayOfWeek) {
                        DayOfWeek.SATURDAY -> 0.7
                        DayOfWeek.SUNDAY -> 0.2
                        else -> 1.0
                    }
                periodQuantity += service.dailyQuantity * dayMultiplier
                serviceDays += current.toString()
            }
            current = current.plus(DatePeriod(days = 1))
        }
    } else {
        val dailyGoal = if (dailyGoalFromRanking > 0.0) dailyGoalFromRanking else company.employeeCount
        periodQuantity = dailyGoal * periodDays
        repeat(periodDays) { serviceDays += it.toString() }
    }

    val metaPeriodo = round(periodQuantity * 0.3 * 100.0) / 100.0
    val metaPeriodoTotal = round(periodQuantity * 100.0) / 100.0
    val divisorDias = serviceDays.size.takeIf { it > 0 } ?: periodDays
    val metaDia = if (divisorDias > 0) round(metaPeriodo / divisorDias * 100.0) / 100.0 else metaPeriodo
    return CompanyPeriodGoals(metaDia, metaPeriodo, metaPeriodoTotal)
}

@Composable
private fun RankingTable(
    title: String,
    rows: List<OperationRankingRow>,
    accentColor: Color,
) {
    ReportCard(titleWithCount(title, rows.size)) {
        if (rows.isEmpty()) {
            Text("Nenhum registro no período selecionado.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        } else {
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val viewportWidth = maxWidth
                Row(Modifier.fillMaxWidth().horizontalScroll(rememberScrollState())) {
                    Box(
                        modifier = Modifier.width(maxOf(viewportWidth, 905.dp)),
                        contentAlignment = Alignment.Center,
                    ) {
                        Column(Modifier.width(905.dp)) {
                            Row(
                                Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.surfaceVariant, AppShapes.small).padding(vertical = 9.dp),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                TableHeader("#", 35.dp)
                                TableHeader("Empresa", 220.dp)
                                TableHeader("Meta/dia", 90.dp)
                                TableHeader("Meta período", 110.dp)
                                TableHeader("Votos", 80.dp)
                                TableHeader("% alcançado", 110.dp)
                                TableHeader("Satisfação", 110.dp)
                                TableHeader("Melhoria", 110.dp)
                            }
                            HorizontalDivider()
                            rows.forEachIndexed { index, row ->
                                Row(
                                    Modifier.fillMaxWidth().padding(vertical = 10.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    TableCell((index + 1).toString(), 35.dp, color = accentColor)
                                    TableCell(row.item.companyName, 220.dp)
                                    TableCell(decimal(row.goals.metaDia), 90.dp)
                                    TableCell(decimal(row.goals.metaPeriodo), 110.dp)
                                    TableCell(row.item.totalVotes.toString(), 80.dp)
                                    TableCell("${decimal(row.achievedPercentage)}%", 110.dp, color = accentColor)
                                    TableCell("${decimal(row.item.satisfaction.percentage)}%", 110.dp)
                                    TableCell("${decimal(row.item.improvement.percentage)}%", 110.dp)
                                }
                                if (index < rows.lastIndex) HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.55f))
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun titleWithCount(
    title: String,
    count: Int,
): String = "$title  •  $count"

@Composable
private fun TableHeader(
    label: String,
    width: androidx.compose.ui.unit.Dp,
) {
    Text(
        label,
        modifier = Modifier.width(width).padding(horizontal = 6.dp),
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = TextAlign.Center,
    )
}

@Composable
private fun TableCell(
    label: String,
    width: androidx.compose.ui.unit.Dp,
    color: Color = MaterialTheme.colorScheme.onSurface,
) {
    Text(
        label,
        modifier = Modifier.width(width).padding(horizontal = 6.dp),
        style = MaterialTheme.typography.bodyMedium,
        color = color,
        textAlign = TextAlign.Center,
    )
}

@Composable
private fun CompanyPicker(
    companies: List<SatisfactionCompany>,
    selected: SatisfactionCompany?,
    onSelect: (SatisfactionCompany) -> Unit,
) {
    SearchableSelect(
        leadingIcon = Lucide.Search,
        options = companies.map { it.name },
        selected = selected?.name.orEmpty(),
        onSelect = { name -> companies.firstOrNull { it.name == name }?.let(onSelect) },
        placeholder = "Selecione uma empresa",
        searchPlaceholder = "Buscar empresa",
        searchable = true,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun SatisfactionPeriodFilter(
    selected: SatisfactionPeriodMode,
    customStart: String,
    customEnd: String,
    onSelect: (SatisfactionPeriodMode, String?, String?) -> Unit,
) {
    var draftStart by remember(selected, customStart) { mutableStateOf(customStart) }
    var draftEnd by remember(selected, customEnd) { mutableStateOf(customEnd) }
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            SatisfactionPeriodMode.entries.forEach { mode ->
                if (mode == selected) {
                    Button(onClick = { onSelect(mode, null, null) }) { Text(mode.label) }
                } else {
                    OutlinedButton(onClick = { onSelect(mode, null, null) }) { Text(mode.label) }
                }
            }
        }
        if (selected == SatisfactionPeriodMode.CUSTOM) {
            Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12), modifier = Modifier.fillMaxWidth()) {
                OutlinedTextField(draftStart, { draftStart = it }, label = { Text("Data inicial") }, modifier = Modifier.weight(1f), singleLine = true)
                OutlinedTextField(draftEnd, { draftEnd = it }, label = { Text("Data final") }, modifier = Modifier.weight(1f), singleLine = true)
            }
            Text("Informe as datas no formato AAAA-MM-DD.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Button(onClick = { onSelect(selected, draftStart, draftEnd) }) { Text("Aplicar período") }
        }
    }
}

private fun satisfactionPeriodBounds(
    mode: SatisfactionPeriodMode,
    today: LocalDate,
): Pair<String, String> =
    when (mode) {
        SatisfactionPeriodMode.DAY -> today.toString() to today.toString()
        SatisfactionPeriodMode.WEEK -> {
            val start = today.minus(DatePeriod(days = today.dayOfWeek.ordinal))
            start.toString() to today.toString()
        }
        SatisfactionPeriodMode.MONTH -> {
            val start = LocalDate(today.year, today.month, 1)
            start.toString() to today.toString()
        }
        SatisfactionPeriodMode.CUSTOM -> today.toString() to today.toString()
    }

@Composable
private fun ReportCard(
    title: String,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    ChartCard(title, modifier) { content() }
}

@Composable
private fun MetricCard(
    title: String,
    value: String,
    subtitle: String,
    color: Color,
    modifier: Modifier,
) {
    Card(
        modifier,
        shape = AppShapes.medium,
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            Modifier.padding(AppSpacing.space16),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(Modifier.width(4.dp).height(52.dp).background(color, AppShapes.small))
            Column {
                Text(title, style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
                Text(value, style = MaterialTheme.typography.headlineMedium, color = color)
                Text(subtitle, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
    }
}

@Composable
private fun BarRow(
    label: String,
    value: Int,
    total: Int,
    color: Color,
) {
    val progress = if (total == 0) 0f else (value.toFloat() / total).coerceIn(0f, 1f)
    Column(Modifier.fillMaxWidth().padding(vertical = 3.dp)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(label)
            Text(value.toString())
        }
        LinearProgressIndicator(progress = { progress }, modifier = Modifier.fillMaxWidth().height(8.dp), color = color)
    }
}

private fun isGoalService(service: SatisfactionServiceVotes): Boolean {
    if (service.typeId == LUNCH_SERVICE_TYPE_ID || service.typeId == DINNER_SERVICE_TYPE_ID) return true
    val text = normalizeMealText("${service.name} ${service.typeId}")
    return text.contains("ALMOCO") || text.contains("JANTAR") || text.contains("JANTA")
}

private fun expectedServiceGoal(
    service: SatisfactionServiceVotes,
    startDate: String,
    endDate: String,
): Double {
    var current = runCatching { LocalDate.parse(startDate) }.getOrNull() ?: return 0.0
    val end = runCatching { LocalDate.parse(endDate) }.getOrNull() ?: current
    var quantity = 0.0
    while (current <= end) {
        val monthKey = current.monthNumber.toString()
        val activeDays = service.activeDaysByMonth
        val isActive = if (activeDays.containsKey(monthKey)) current.dayOfMonth in activeDays.getValue(monthKey) else true
        if (isActive) {
            val multiplier =
                when (current.dayOfWeek) {
                    DayOfWeek.SATURDAY -> 0.7
                    DayOfWeek.SUNDAY -> 0.2
                    else -> 1.0
                }
            quantity += service.dailyQuantity * multiplier
        }
        current = current.plus(DatePeriod(days = 1))
    }
    return kotlin.math.round(quantity * 0.3 * 100.0) / 100.0
}

private fun servicePercent(
    service: SatisfactionServiceVotes,
    rating: String,
): Double {
    service.percentages[rating]?.let { return it }
    return if (service.total == 0) 0.0 else (service.ratings[rating] ?: 0) * 100.0 / service.total
}

private fun serviceEmoji(name: String): String =
    when (normalizeMealText(name)) {
        "DESJEJUM" -> "\uD83E\uDD50"
        "ALMOCO" -> "\uD83C\uDF7D\uFE0F"
        "JANTA", "JANTAR" -> "\uD83C\uDF72"
        "CEIA" -> "\uD83C\uDF19"
        "MERENDA" -> "\uD83C\uDF4E"
        else -> "\uD83D\uDCCA"
    }

private fun ratingColor(rating: String): Color =
    when (rating) {
        "Ótimo" -> excellentColor
        "Bom" -> goodColor
        "Regular" -> regularColor
        else -> poorColor
    }

@Composable
private fun SatisfactionPage(
    title: String,
    error: String?,
    content: @Composable () -> Unit,
) {
    AppScrollColumn(
        modifier = Modifier.fillMaxWidth(),
        contentPadding =
            androidx.compose.foundation.layout
                .PaddingValues(AppSpacing.space24),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        footer = { AppFooter() },
    ) {
        Text(title, style = MaterialTheme.typography.headlineMedium)
        error?.let { Text(it, color = MaterialTheme.colorScheme.error) }
        content()
    }
}

@Composable
private fun EmptySatisfactionState(
    title: String,
    description: String,
) {
    Card(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(AppSpacing.space24)) {
            Text(title, style = MaterialTheme.typography.titleLarge)
            Text(description, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
private fun SectionTitle(title: String) {
    Text(title, style = MaterialTheme.typography.titleLarge)
}

private fun decimal(value: Double): String = value.toLocaleStringPtBr()

private fun formatSatisfactionDate(value: String): String {
    val date = runCatching { LocalDate.parse(value.take(10)) }.getOrNull() ?: return value
    return "${date.dayOfMonth.toString().padStart(2, '0')}/${date.monthNumber.toString().padStart(2, '0')}/${date.year}"
}

private fun Double.toLocaleStringPtBr(): String {
    val rounded = kotlin.math.round(this * 10) / 10
    return if (rounded % 1.0 == 0.0) rounded.toInt().toString() else rounded.toString().replace('.', ',')
}

private fun periodDaysBetween(
    startDate: String,
    endDate: String,
): Int {
    val start = runCatching { LocalDate.parse(startDate) }.getOrNull() ?: return 1
    val end = runCatching { LocalDate.parse(endDate) }.getOrNull() ?: return 1
    return (end.toEpochDays() - start.toEpochDays() + 1).coerceAtLeast(1).toInt()
}

private fun List<SatisfactionRankingItem>.toOperationStatus(): OperationStatus {
    val totalVotes = sumOf { it.totalVotes }
    val satisfactionCount = sumOf { it.satisfaction.count }
    val improvementCount = sumOf { it.improvement.count }

    fun metric(count: Int) = SatisfactionMetric(count, if (totalVotes == 0) 0.0 else count * 100.0 / totalVotes)
    return OperationStatus(totalVotes, metric(satisfactionCount), metric(improvementCount))
}

private fun satisfactionLevel(value: Double): String =
    when {
        value >= 80 -> "Excelente"
        value >= 60 -> "Bom"
        value >= 40 -> "Regular"
        else -> "Precisa melhorar"
    }

private fun exportReportCsv(
    company: SatisfactionCompany?,
    report: SatisfactionReport?,
): String {
    if (company == null || report == null) return "Nenhum relatório disponível para exportar."
    val lines =
        buildList {
            add("Empresa;Data;Ótimo;Bom;Regular;Ruim;Total")
            report.dailyVotes.forEach { day -> add("${company.name.csv()};${day.date};${day.excellent};${day.good};${day.regular};${day.poor};${day.total}") }
            add("")
            add("Serviço;Total;Ótimo;Bom;Regular;Ruim")
            report.serviceVotes.forEach { service ->
                add("${service.name.csv()};${service.total};${service.ratings["Ótimo"] ?: 0};${service.ratings["Bom"] ?: 0};${service.ratings["Regular"] ?: 0};${service.ratings["Ruim"] ?: 0}")
            }
            add("")
            add("Avaliação;Serviço;Comentário;Data")
            report.negativeVotes.forEach { vote -> add("${vote.rating};${vote.serviceName.csv()};${vote.comment.csv()};${vote.date}") }
        }
    return if (saveFile("satisfaction-${company.name.safeFileName()}.csv", "text/csv;charset=utf-8", lines.joinToString("\n").encodeToByteArray())) {
        "CSV exportado com sucesso."
    } else {
        "Não foi possível exportar o CSV nesta plataforma."
    }
}

private fun exportRankingCsv(
    rows: List<SatisfactionRankingItem>,
    startDate: String,
    endDate: String,
): String {
    val lines =
        buildList {
            add("Período;$startDate a $endDate")
            add("Posição;Empresa;Meta/dia;Votos;Satisfação;Melhoria")
            rows.forEach { row -> add("${row.position};${row.companyName.csv()};${decimal(row.dailyMeals)};${row.totalVotes};${decimal(row.satisfaction.percentage)}%;${decimal(row.improvement.percentage)}%") }
        }
    return if (saveFile("satisfaction-status-$startDate-$endDate.csv", "text/csv;charset=utf-8", lines.joinToString("\n").encodeToByteArray())) {
        "CSV exportado com sucesso."
    } else {
        "Não foi possível exportar o CSV nesta plataforma."
    }
}

private fun exportReportPdf(
    company: SatisfactionCompany?,
    report: SatisfactionReport?,
): String {
    if (company == null || report == null) return "Nenhum relatório disponível para exportar."
    val lines =
        buildList {
            add(PdfLine.Title("Relatório de satisfação - ${company.name}"))
            add(PdfLine.Row("Total de votos", report.totalVotes.toString(), bold = true))
            add(PdfLine.Heading("Pesquisa diária"))
            report.dailyVotes.forEach { day -> add(PdfLine.Row(formatSatisfactionDate(day.date), "${day.total} votos (ótimo ${day.excellent}, bom ${day.good}, regular ${day.regular}, ruim ${day.poor})")) }
            add(PdfLine.Heading("Análise por serviço"))
            report.serviceVotes.forEach { service -> add(PdfLine.Row(service.name, "${service.total} votos")) }
            add(PdfLine.Heading("Respostas negativas"))
            report.negativeVotes.forEach { vote -> add(PdfLine.Text("${vote.rating} - ${vote.serviceName}: ${vote.comment.ifBlank { "Sem comentário" }} (${formatSatisfactionDate(vote.date)})")) }
        }
    return if (savePdf("satisfaction-${company.name.safeFileName()}.pdf", encodeBase64(PdfWriter.build(PdfReport(lines))))) {
        "PDF exportado com sucesso."
    } else {
        "Não foi possível exportar o PDF nesta plataforma."
    }
}

private fun exportRankingPdf(
    rows: List<SatisfactionRankingItem>,
    startDate: String,
    endDate: String,
): String {
    val lines =
        buildList {
            add(PdfLine.Title("Status da operação - satisfação"))
            add(PdfLine.Text("Período: $startDate a $endDate"))
            add(PdfLine.Heading("Ranking de empresas"))
            rows.forEach { row -> add(PdfLine.Row("${row.position}. ${row.companyName}", "${row.totalVotes} votos | sat. ${decimal(row.satisfaction.percentage)}%")) }
        }
    return if (savePdf("satisfaction-status-$startDate-$endDate.pdf", encodeBase64(PdfWriter.build(PdfReport(lines))))) {
        "PDF exportado com sucesso."
    } else {
        "Não foi possível exportar o PDF nesta plataforma."
    }
}

private fun String.csv(): String = "\"${replace("\"", "\"\"")}\""

private fun String.safeFileName(): String = map { if (it.isLetterOrDigit() || it == '-' || it == '_' || it == '.') it else '_' }.joinToString("")
